import { AbstractControl, ValidationErrors } from "@angular/forms";
import { Part } from "../interface/part";
import { ApiFloat } from "./api-float";
import { Utilities } from "./utilities";

export class PartUtilities extends Utilities {

  /**
   * @deprecated
   * Use the form's dirty and touched states to determine if the part has been changed instead of this method.
   */
  static changed(part: Part, formValue: Part): boolean {
    return !this.equalString(part.description, formValue.description)
      || !this.equalString(part.code, formValue.code)
      || !this.equalString(part.brand, formValue.brand)
      || !this.equalNumberWithDefault(part.purchaseValue, formValue.purchaseValue, 0)
      || !this.equalNumberWithDefault(part.saleValue, formValue.saleValue, 0)
      || (part.group && part.group.length ? part.group[0] != formValue.groupObject._id : false)
      || (part.subGroup && part.subGroup.length ? part.subGroup[0] != formValue.subgroupObject._id : false)
      || (part.application && part.application.length ? part.application[0] != formValue.applicationObject._id : false)
      || part.EANcode != formValue.EANcode
      || part.unitOfMeasurements != formValue.unitOfMeasurements
      || part.grossWeight != formValue.grossWeight
      || part.liquidWeight != formValue.liquidWeight
      || part.useIn != formValue.useIn
      || part.NCM != formValue.NCM
      || part.CEST != Number(formValue.CEST)
      || part.cost != formValue.cost
      || part.averageCost != formValue.averageCost
      || part.allocation != formValue.allocation
      || part.fabricCode != formValue.fabricCode
      || part.availableStock != formValue.availableStock
      || part.stockMin != formValue.stockMin
      || part.orderStock != formValue.orderStock
      || part.osStock != formValue.osStock
      || part.origin != formValue.origin
      || part.ANP != formValue.ANP
      || part.readjustmentPercentage != formValue.readjustmentPercentage
      || part.ICMS != formValue.ICMS
      || part.PIS != formValue.PIS
      || part.COFINS != formValue.COFINS
      || part.superSimples != formValue.superSimples
      || part.IPI != formValue.IPI
      || part.ICMS_ST != formValue.ICMS_ST
      || JSON.stringify(part.itemRelated) != JSON.stringify(formValue.itemRelated)
  }

  /** 
   * @deprecated 
   * The Part interface should be updated to reflect the new API structure.
   */
  static complyApp(obj: any): Part {
    let part: Part = obj;
    part.description = obj.description || '';
    part.saleValue = obj.saleValue || 0;
    part.EANcode = obj.CodeEAN;
    part.unitOfMeasurements = obj.unitMeasure;
    part.readjustmentPercentage = obj.salePricePercentage;
    part.allocation = obj.location
    part.itemRelated = obj.itemRelated || [];

    part['cost'] = Number(obj.cost) || 0;
    part['stock'] = Number(obj.stock) || 0;
    part['availableStock'] = Number(obj.availableStock) || 0;
    part['orderStock'] = Number(obj.orderStock) || 0;
    part['osStock'] = Number(obj.osStock) || 0;
    part['stockMin'] = Number(obj.minStock) || 0;
    part['initStock'] = Number(obj.initStock) || 0;

    part.ICMS = {
      CST: obj.ICMS ? obj.ICMS.CST : undefined,
      Aliquot: obj.ICMS ? obj.ICMS.Aliquot : undefined,
      Message: obj.ICMS ? obj.ICMS.Message : undefined,
      CFOP: obj.ICMS ? obj.ICMS.CFOP : undefined,
      CFOP_I: obj.ICMS ? obj.ICMS.CFOP_I : undefined,
      readjustment: obj.ICMS ? obj.ICMS.readjustment : undefined
    }
    part.PIS = {
      CST: obj.PIS && obj.PIS.CST ? (obj.PIS.CST || '').padStart(2, '0') : undefined,
      Aliquot: obj.PIS ? obj.PIS.Aliquot : undefined,
      Message: obj.PIS ? obj.PIS.Message : undefined,
    }
    part.COFINS = {
      CST: obj.COFINS && obj.COFINS.CST ? (obj.COFINS.CST || '').padStart(2, '0') : undefined,
      Aliquot: obj.COFINS ? obj.COFINS.Aliquot : undefined,
      Message: obj.COFINS ? obj.COFINS.Message : undefined,
    }
    part.ICMS_ST = {
      mva: obj.ICMS_ST ? obj.ICMS_ST.mva : undefined,
      Aliquot: obj.ICMS_ST ? obj.ICMS_ST.Aliquot : undefined,
      readjustmentST: obj.ICMS_ST ? obj.ICMS_ST.readjustmentST : undefined
    }

    if (part.purchaseValue === undefined) {
      part.purchaseValue = null;
    }
    return part;
  }

  /**
   * @deprecated
   * The Part interface should be updated to reflect the new API structure.
   */
  static complyAPI(part: Part, updatePartStockFields: boolean): any {
    let apiPart = {
      ...part,
      code: `${part.code}`?.trim()
    } as any;

    apiPart['brand'] = part.brand ?? null;
    apiPart['useIn'] = part.useIn;
    apiPart['description'] = part.description;
    apiPart['saleValue'] = ApiFloat(apiPart.saleValue) || 0;

    apiPart['CodeEAN'] = part.EANcode || "SEM GTIN";
    delete apiPart.EANcode;

    apiPart['unitMeasure'] = part.unitOfMeasurements;
    delete apiPart.unitOfMeasurements;

    apiPart['salePricePercentage'] = part.readjustmentPercentage;
    delete apiPart.readjustmentPercentage;

    apiPart['NCM'] = Number(part.NCM ? part.NCM.toString() : "0");

    apiPart['location'] = part.allocation
    delete apiPart.allocation

    apiPart['cost'] = ApiFloat(part.cost || 0, 3);

    if (updatePartStockFields) {
      apiPart['stock'] = ApiFloat(part.stock || 0, 3);
      apiPart['availableStock'] = ApiFloat(part.availableStock || 0, 3);
      apiPart['orderStock'] = ApiFloat(part.orderStock || 0, 3);
      apiPart['osStock'] = ApiFloat(part.osStock || 0, 3);
      apiPart['initStock'] = ApiFloat(part.initStock || 0, 3);
    } else {
      delete apiPart['stock'];
      delete apiPart['availableStock'];
      delete apiPart['orderStock'];
      delete apiPart['osStock'];
      delete apiPart['initStock'];
    }

    if (typeof part.stockMin === "number") apiPart['minStock'] = ApiFloat(part.stockMin || 0, 3);

    if (part.CEST) {
      apiPart['CEST'] = (part.CEST.toString().replace(/\./g, ''));
    }

    apiPart['IPI'] = part.IPI ?? '';

    if (part.parts) {
      // similars
      apiPart['parts'] = part.parts.filter(value => Boolean(value)).map(item => {
        if (typeof item == 'string') {
          return item;
        } else {
          return item.id;
        }
      })
    }

    for (const tax of ['ICMS', 'PIS', 'COFINS']) {
      if (part[tax]?.CST === null || part[tax]?.CST === undefined) {
        Object.assign(apiPart, { ...part[tax] || {}, CST: "" });
      }
    }

    if (part.groupObject || part.group) {
      apiPart["group"] = [part.groupObject?._id ?? part.group?.[0]];
    } else {
      apiPart["group"] = [];
    }
    delete apiPart['groupObject'];

    if (part.subgroupObject || part.subGroup) {
      apiPart["subGroup"] = [part.subgroupObject?._id ?? part.subGroup?.[0]];
    } else {
      apiPart["subGroup"] = [];
    }

    delete apiPart['subgroupObject'];

    if (part.applicationObject || part.application) {
      apiPart["application"] = [part.applicationObject?._id ?? part.application?.[0]];
    } else {
      apiPart["application"] = [];
    }
    delete apiPart['applicationObject'];

    return apiPart;
  }

  static cestValidator() {
    return (control: AbstractControl<number | string>): ValidationErrors | null => {
      const contorlValue = control.value;
      if (!contorlValue) {
        return null;
      }

      if (contorlValue.toString()?.length === 7 && Utilities.stringHaveOnlyEqualCharacters(contorlValue?.toString())) {
        control.markAsTouched();
        return { equalDigits: true };
      }

      if (contorlValue.toString().length !== 7) {
        control.markAsTouched();
        return { cestInvalid: true };
      }

      return null;
    }
  }

  static formatCESTToString(cestValue: number | string) {
    if (!cestValue) {
      return cestValue;
    }

    let cestValueAsString = cestValue?.toString()?.trim();

    while (cestValueAsString.length < 7) {
      cestValueAsString = "0" + cestValueAsString;
    }

    return cestValueAsString;
  }

  static isDigitsOnly(s: string) {
    return /^\d+$/.test(s);
  }

  /** 
   * ## Validação do dígito verificador (regra geral)
   * 
   * ### EAN-13
   * - Pegue os 12 primeiros dígitos (sem o último).
   * - Some os dígitos em posições ímpares (1ª, 3ª, 5ª… contando da esquerda) com peso 1.
   * - Some os dígitos em posições pares (2ª, 4ª, 6ª…) com peso 3.
   * - Some tudo.
   * - Dígito verificador = (10 − (soma % 10)) % 10
   * - Compare com o 13º dígito.
   * 
   * ### EAN-8
   * - Pegue os 7 primeiros dígitos.
   * - Pesos alternados da esquerda: 3, 1, 3, 1, 3, 1, 3
   * - Mesma fórmula do dígito: (10 − (soma % 10)) % 10
   * - Compare com o 8º dígito.
   */
  static validateEAN(codeRaw: string): boolean {
    const code = (codeRaw ?? "").trim();

    if (!PartUtilities.isDigitsOnly(code)) return false;
    if (code.length !== 8 && code.length !== 13) return false;

    const digits = code.split("").map((c) => Number(c));

    if (digits.some((n) => Number.isNaN(n))) return false;

    if (code.length === 13) {
      // EAN-13: usa os 12 primeiros
      let sum = 0;
      for (let i = 0; i < 12; i++) {
        const positionFromLeft = i + 1; // 1..12
        const weight = positionFromLeft % 2 === 0 ? 3 : 1; // par=3, ímpar=1
        sum += digits[i] * weight;
      }
      const check = (10 - (sum % 10)) % 10;
      return check === digits[12];
    }

    // EAN-8: usa os 7 primeiros e compara com o 8º
    let sum = 0;
    for (let i = 0; i < 7; i++) {
      const weight = i % 2 === 0 ? 3 : 1; // 1º (i=0)=3, 2º=1, ...
      sum += digits[i] * weight;
    }
    const check = (10 - (sum % 10)) % 10;
    return check === digits[7];
  }

}
